<template>
  <div>
    <!-- Begin page -->
    <div id="layout-wrapper">
      <NavigationBar />
      <!-- ============================================================== -->
      <!-- Start right Content here -->
      <!-- ============================================================== -->
      <div class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">
            <!-- start page title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Category & Role Management</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript:void(0)" @click="goBack">Employee Management</a></li>
                      <li class="breadcrumb-item active">Category & Roles</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
            <!-- end page title -->

            <!-- Enhanced stats cards -->
            <div class="row">
              <!-- Total Categories -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card mini-stats-wid h-100">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="text-muted mb-2">Total Categories</p>
                        <h3 class="mb-0 text-primary">{{statistics.totalCategories}}</h3>
                        <small class="text-muted">All categories</small>
                      </div>
                      <div class="avatar-sm ms-auto">
                        <div class="avatar-title bg-primary bg-gradient rounded-circle text-white font-size-20">
                          <i class="mdi mdi-folder-multiple"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Active Categories -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card mini-stats-wid h-100">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="text-muted mb-2">Active Categories</p>
                        <h3 class="mb-0 text-success">{{statistics.activeCategories}}</h3>
                        <small class="text-muted">Currently active</small>
                      </div>
                      <div class="avatar-sm ms-auto">
                        <div class="avatar-title bg-success bg-gradient rounded-circle text-white font-size-20">
                          <i class="mdi mdi-folder-check"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Total Users -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card blog-stats-wid h-100">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="text-muted mb-2">Total Users</p>
                        <h3 class="mb-0 text-info">{{statistics.totalUsers}}</h3>
                        <small class="text-muted">In all categories</small>
                      </div>
                      <div class="avatar-sm ms-auto">
                        <div class="avatar-title bg-info bg-gradient rounded-circle text-white font-size-20">
                          <i class="mdi mdi-account-multiple"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Roles Assigned -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card blog-stats-wid h-100">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="text-muted mb-2">Roles Assigned</p>
                        <h3 class="mb-0 text-warning">{{statistics.totalRolesAssigned}}</h3>
                        <small class="text-muted">Total assignments</small>
                      </div>
                      <div class="avatar-sm ms-auto">
                        <div class="avatar-title bg-warning bg-gradient rounded-circle text-white font-size-20">
                          <i class="mdi mdi-shield-account"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Main categories section -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-white pt-0 pr-0 p-0 d-flex justify-content-between align-items-center w-100 border-bottom">
                    <div class="btn-toolbar p-3 d-flex justify-content-between align-items-center w-100" role="toolbar">
                      <div class="flex-grow-1">
                        <div class="search-box mb-0 me-3">
                          <div class="position-relative">
                            <i v-if="!loading" class="bx bx-search-alt search-icon"></i>
                            <div v-else class="search-icon">
                              <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
                            </div>
                            <input v-model="searchQuery"
                                   type="text"
                                   class="form-control bg-light border-light rounded"
                                   :class="{'is-loading': loading}"
                                   placeholder="Search categories..."
                                   spellcheck="false">
                            <button v-if="searchQuery"
                                    @click="searchQuery = ''"
                                    class="btn btn-link clear-btn"
                                    type="button">
                              <i class="mdi mdi-close"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      <div class="d-flex flex-wrap">
                        <a @click="openAddCategoryModal" href="javascript: void(0);" type="button"
                           class="btn btn-success option-selector">
                          <i class="mdi mdi-plus-circle-outline font-size-16"></i>
                          <span class="pl-1 d-md-inline">Add Category</span>
                        </a>
                      </div>
                    </div>
                  </div>

                  <div class="card-body">
                    <div v-if="loading" class="text-center p-5">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      <p class="mt-2">Loading categories...</p>
                    </div>

                    <div v-else-if="filteredCategories.length === 0" class="text-center p-5">
                      <div class="avatar-md mx-auto mb-4">
                        <div class="avatar-title bg-light rounded-circle text-primary">
                          <i class="mdi mdi-folder-off font-size-24"></i>
                        </div>
                      </div>
                      <h5 class="mt-4">No categories found</h5>
                      <p class="text-muted">Try adjusting your search or create a new category</p>
                      <button @click="openAddCategoryModal" class="btn btn-success mt-3">
                        <i class="mdi mdi-plus me-1"></i>Create First Category
                      </button>
                    </div>

                    <div v-else class="row">
                      <div v-for="category in filteredCategories" :key="category.id" class="col-xl-4 col-md-6 mb-4">
                        <div class="card category-card h-100">
                          <div class="card-body">
                            <div class="d-flex justify-content-between align-items-start mb-3">
                              <div>
                                <h5 class="card-title mb-1">{{ category.categoryName }}</h5>
                                <span :class="getStatusBadgeClass(category.status)">
                                  {{ category.status }}
                                </span>
                              </div>
                              <div class="dropdown">
                                <button class="btn btn-light btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown">
                                  <i class="mdi mdi-dots-vertical"></i>
                                </button>
                                <ul class="dropdown-menu">
                                  <li><a @click="viewCategory(category)" class="dropdown-item" href="javascript:void(0)">
                                    <i class="mdi mdi-eye me-2"></i>View Details
                                  </a></li>
                                  <li><a @click="editCategory(category)" class="dropdown-item" href="javascript:void(0)">
                                    <i class="mdi mdi-pencil me-2"></i>Edit
                                  </a></li>
                                  <li><a @click="manageRoles(category)" class="dropdown-item" href="javascript:void(0)">
                                    <i class="mdi mdi-shield-account me-2"></i>Manage Roles
                                  </a></li>
                                  <li><hr class="dropdown-divider"></li>
                                  <li><a @click="deleteCategory(category)" class="dropdown-item text-danger" href="javascript:void(0)">
                                    <i class="mdi mdi-delete me-2"></i>Delete
                                  </a></li>
                                </ul>
                              </div>
                            </div>

                            <p v-if="category.description" class="text-muted small mb-3">
                              {{ category.description }}
                            </p>

                            <div class="row text-center mb-3">
                              <div class="col-4">
                                <h5 class="mb-0">{{ category.currentUsers }}</h5>
                                <small class="text-muted">Users</small>
                              </div>
                              <div class="col-4">
                                <h5 class="mb-0">{{ category.usersLimit || '∞' }}</h5>
                                <small class="text-muted">Limit</small>
                              </div>
                              <div class="col-4">
                                <h5 class="mb-0">{{ category.rolesCount }}</h5>
                                <small class="text-muted">Roles</small>
                              </div>
                            </div>

                            <div v-if="category.usersLimit > 0" class="mb-3">
                              <div class="d-flex justify-content-between mb-1">
                                <small class="text-muted">Capacity</small>
                                <small class="text-muted">{{ getCapacityPercentage(category) }}%</small>
                              </div>
                              <div class="progress" style="height: 6px;">
                                <div
                                    class="progress-bar"
                                    :class="getProgressBarClass(category)"
                                    :style="{width: getCapacityPercentage(category) + '%'}"
                                ></div>
                              </div>
                            </div>

                            <div class="d-grid gap-2">
                              <button @click="manageRoles(category)" class="btn btn-outline-primary btn-sm">
                                <i class="mdi mdi-shield-account me-1"></i>Manage Roles
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- end col -->
            </div>
            <!-- end row -->
          </div>
          <!-- container-fluid -->
        </div>
        <!-- End Page-content -->

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6">
                © 2025 Category & Role Management System | {{statistics.totalCategories}} Total Categories
              </div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">
                  Crafted with <i class="mdi mdi-heart text-danger"></i> by HR Team
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
      <!-- end main content-->
    </div>
    <!-- end main content-->

    <!-- Modals -->
    <AddEditCategoryModal
        v-if="showAddEditModal"
        :category="selectedCategory"
        :mode="modalMode"
        @close="closeAddEditModal"
        @saved="handleCategorySaved"
    />

    <ManageRolesModal
        v-if="showManageRolesModal"
        :category="selectedCategory"
        :all-roles="allRoles"
        @close="closeManageRolesModal"
        @saved="handleRolesSaved"
    />

    <ViewCategoryModal
        v-if="showViewModal"
        :category="selectedCategory"
        @close="closeViewModal"
        @edit="editCategoryFromView"
        @manage-roles="manageRolesFromView"
    />
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import AddEditCategoryModal from "@/components/employeeRegister/modals/AddEditCategoryModal.vue";
import ManageRolesModal from "@/components/employeeRegister/modals/ManageRolesModal.vue";
import ViewCategoryModal from "@/components/employeeRegister/modals/ViewCategoryModal.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "CategoryRoleManagement",
  components: {
    NavigationBar,
    AddEditCategoryModal,
    ManageRolesModal,
    ViewCategoryModal
  },
  data() {
    return {
      loading: false,
      categories: [],
      allRoles: [],
      searchQuery: '',

      showAddEditModal: false,
      showManageRolesModal: false,
      showViewModal: false,

      selectedCategory: null,
      modalMode: 'add',

      statistics: {
        totalCategories: 0,
        activeCategories: 0,
        totalUsers: 0,
        totalRolesAssigned: 0
      }
    };
  },
  computed: {
    filteredCategories() {
      if (!this.searchQuery) {
        return this.categories;
      }

      const query = this.searchQuery.toLowerCase();
      return this.categories.filter(cat =>
          cat.categoryName.toLowerCase().includes(query) ||
          (cat.description && cat.description.toLowerCase().includes(query))
      );
    }
  },
  mounted() {
    this.loadCategories();
    this.loadAllRoles();
    this.loadStatistics();
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },

    loadCategories() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getCategories");

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.categories = res.data.data.categories || [];
            } else {
              this.$toast.error(res.data.message || "Failed to load categories");
            }
          })
          .catch((e) => {
            this.loading = false;
            this.$toast.error("Error loading categories: " + e.message);
          });
    },

    loadAllRoles() {
      const data = new FormData();
      data.append("function", "getAllRoles");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.allRoles = res.data.data.roles || [];
            }
          })
          .catch((e) => {
            console.error("Error loading roles:", e);
          });
    },

    loadStatistics() {
      const data = new FormData();
      data.append("function", "getCategoryStatistics");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.statistics = res.data.data.statistics;
            }
          })
          .catch((e) => {
            console.error("Error loading statistics:", e);
          });
    },

    openAddCategoryModal() {
      this.modalMode = 'add';
      this.selectedCategory = null;
      this.showAddEditModal = true;
    },

    editCategory(category) {
      this.modalMode = 'edit';
      this.selectedCategory = category;
      this.showAddEditModal = true;
    },

    editCategoryFromView(category) {
      this.closeViewModal();
      setTimeout(() => {
        this.editCategory(category);
      }, 300);
    },

    viewCategory(category) {
      this.selectedCategory = category;
      this.showViewModal = true;
    },

    manageRoles(category) {
      this.selectedCategory = category;
      this.showManageRolesModal = true;
    },

    manageRolesFromView(category) {
      this.closeViewModal();
      setTimeout(() => {
        this.manageRoles(category);
      }, 300);
    },

    deleteCategory(category) {
      if (!confirm(`Are you sure you want to delete the category "${category.categoryName}"?`)) {
        return;
      }

      const data = new FormData();
      data.append("function", "deleteCategory");
      data.append("categoryId", category.id);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.$toast.success("Category deleted successfully");
              this.loadCategories();
              this.loadStatistics();
            } else {
              this.$toast.error(res.data.message || "Failed to delete category");
            }
          })
          .catch((e) => {
            this.$toast.error("Error deleting category: " + e.message);
          });
    },

    closeAddEditModal() {
      this.showAddEditModal = false;
      this.selectedCategory = null;
    },

    closeManageRolesModal() {
      this.showManageRolesModal = false;
      this.selectedCategory = null;
    },

    closeViewModal() {
      this.showViewModal = false;
      this.selectedCategory = null;
    },

    handleCategorySaved() {
      this.closeAddEditModal();
      this.loadCategories();
      this.loadStatistics();
    },

    handleRolesSaved() {
      this.closeManageRolesModal();
      this.loadCategories();
    },

    getStatusBadgeClass(status) {
      return status === 'active' ? 'badge bg-success' : 'badge bg-secondary';
    },

    getCapacityPercentage(category) {
      if (!category.usersLimit || category.usersLimit === 0) {
        return 0;
      }
      const percentage = (category.currentUsers / category.usersLimit) * 100;
      return Math.min(Math.round(percentage), 100);
    },

    getProgressBarClass(category) {
      const percentage = this.getCapacityPercentage(category);
      if (percentage >= 90) return 'bg-danger';
      if (percentage >= 75) return 'bg-warning';
      if (percentage >= 50) return 'bg-info';
      return 'bg-success';
    }
  }
};
</script>

<style scoped>
.badge {
  font-weight: 500;
  padding: 0.4em 0.6em;
  font-size: 0.75rem;
}

.avatar-sm {
  width: 50px;
  height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.search-box .form-control {
  padding-left: 40px;
  padding-right: 40px;
}

.search-box .search-icon {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
  z-index: 10;
}

.search-box .clear-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  padding: 0;
  color: #dc3545;
  text-decoration: none;
  border: none;
  background: none;
}

.search-box .clear-btn:hover {
  color: #b02a37;
}

.search-icon .spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.search-box .form-control.is-loading {
  background-color: #f8f9fa !important;
}

.category-card {
  transition: transform 0.2s, box-shadow 0.2s;
  border: none;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
}

.category-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.1);
}

.bg-gradient {
  background-image: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%);
}

.progress {
  border-radius: 10px;
  overflow: hidden;
}

.progress-bar {
  transition: width 0.6s ease;
}

.dropdown-toggle::after {
  display: none;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
}

@media (max-width: 768px) {
  .search-box {
    margin-bottom: 1rem;
  }

  .btn-toolbar {
    flex-direction: column;
  }
}

.card {
  border-radius: 0.75rem;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
  border: none;
}

.card-header {
  border-radius: 0.75rem 0.75rem 0 0;
}

.footer {
  background-color: #f8f9fa;
  padding: 20px 0;
  border-top: 1px solid #e9ecef;
}
</style>